import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';

interface Object_Simple {
    id: number;
    name: string;
}

/**
 * The dialog store for the manager
 */
class ObjectDialogStore {
    private sdk: ApplicationSdk;

    public activeTab: number = 0;
    public state: string = 'add';

    public object_id: number | null = null;

    public object_number: number | null = null;
    public object_name: string = '';
    public object_street: string = '';
    public object_housenumber: string = '';
    public object_zipcode: string = '';
    public object_city: string = '';
    public object_note: string = '';
    public object_archived: boolean = false;
    public object_representation: boolean = false;

    public object_assigned_employees: Set<number> = new Set();
    public object_assigned_products: Set<number> = new Set();
    public object_excluded_products: Set<number> = new Set();

    public selected_manager_id: number | null = null; // Selected manager ID
    public available_managers: Array<{ id: number; name: string }> = []; // List of available managers

    public objectDataLoading: boolean = false;

    public employeeDataFetched: boolean = false;
    public employeeDataLoading: boolean = false;
    public assignedEmployees: Set<number> = new Set();
    public unassignedEmployees: Set<number> = new Set();
    public availableEmployees: Map<number, string> = new Map();

    public productDataFetched: boolean = false;
    public productDataLoading: boolean = false;
    public assignedProducts: Set<number> = new Set();
    public unassignedProducts: Set<number> = new Set();
    public availableProducts: Map<number, any> = new Map();

    public showArchiveConfirmation: boolean = false;

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            activeTab: observable,
            object_id: observable,
            object_number: observable,
            object_name: observable,
            state: observable,
            object_street: observable,
            showArchiveConfirmation: observable,
            object_housenumber: observable,
            object_zipcode: observable,
            object_city: observable,
            object_note: observable,
            selected_manager_id: observable,
            available_managers: observable,
            object_assigned_employees: observable,
            object_assigned_products: observable,
            object_excluded_products: observable,
            employeeDataLoading: observable,
            assignedEmployees: observable,
            unassignedEmployees: observable,
            availableEmployees: observable,
            objectDataLoading: observable,
            productDataLoading: observable,
            assignedProducts: observable,
            unassignedProducts: observable,
            availableProducts: observable,
            object_archived: observable,
            object_representation: observable,
            onChangeTab: action,
            selectManager: action,
            archiveObject: action,
            fetchEmployees: action,
            fetchObject: action,
            clone: action,
            setShowArchiveConfirmation: action,
            fetchAvailableManagers: action,
            updateEmployeeAssignment: action,
            updateProductsAssignment: action,
            resetProductsAssignment: action,
            onSaveObject: action,
            objectname: computed,
            objectnote: computed,
            street: computed,
            housenumber: computed,
            zipcode: computed,
            city: computed,
            assignedEmployeeArray: computed,
            unassignedEmployeeArray: computed,
            assignedProductsArray: computed,
            unassignedProductsArray: computed,
            archived: computed,
            id: computed,
            isCloneable: computed,
            isArchivable: computed,
            isRepresentation: computed,
            isLoading: computed,
            validObjectname: computed,
            validObjectId: computed,
            isInvalid: computed,
        });

        this.sdk = sdk;
    }

    // Clone the current product
    public clone(): void {
        this.object_id = null;
        this.state = 'add';
        this.object_name = `${this.object_name} (Kopie)`;
    }

    public setShowArchiveConfirmation(show: boolean): void {
        this.showArchiveConfirmation = show;
    }

    public async init(config: any): Promise<void> {
        if (config?.object_id) {
            this.object_id = config.object_id;
            this.state = 'update';
        }
        await this.fetchObject(config?.object_id || null);
        await this.fetchEmployees();
    }

    public async fetchAvailableManagers(force: boolean = false): Promise<void> {
        if (!force) {
            if (this.available_managers.length > 1) {
                return;
            }
        }
        const managerResponse: any = await this.sdk.fetchApi('manager', 'query', {});

        runInAction(() => {
            if (managerResponse?.status === 200) {
                this.available_managers = managerResponse.json.data.map((manager: any) => ({
                    id: manager.id,
                    name: `${manager.firstname} ${manager.lastname}`,
                }));
            }
        });
    }

    public async fetchEmployees(): Promise<void> {
        if (this.employeeDataFetched) {
            return;
        }
        // Reset
        runInAction(() => {
            this.employeeDataLoading = true;
            this.assignedEmployees = new Set();
            this.unassignedEmployees = new Set();
        });

        const employeeList: any = await this.sdk.fetchApi('employee', 'query', {
            format: 'simple',
            limit: 999,
        });

        runInAction(() => {
            this.employeeDataLoading = false;

            if (employeeList?.status === 200) {
                this.employeeDataFetched = true;
                (employeeList.json.data as Array<any>).forEach((employee: any) => {
                    if (!this.availableEmployees.has(employee.id)) {
                        this.availableEmployees.set(
                            employee.id,
                            `${employee.firstname} ${employee.lastname}`
                        );
                    }

                    // add this object to unassigned if it is not already assigned
                    if (this.object_assigned_employees.has(employee.id)) {
                        this.assignedEmployees.add(employee.id);
                    } else {
                        this.unassignedEmployees.add(employee.id);
                    }
                });
            }
        });
    }

    public async fetchObject(id: number | null): Promise<void> {
        if (!id) {
            return this.fetchNewObject();
        }

        runInAction(() => {
            this.objectDataLoading = true;
        });

        const objectDetails: any = await this.sdk.fetchApi('object', 'get', { object_id: id });

        runInAction(() => {
            this.objectDataLoading = false;
            if (objectDetails?.status === 200) {
                const { object, assigned_employees, assigned_products } = objectDetails.json || {};

                if (object) {
                    this.object_id = object.id;
                    this.object_number = object.object_number;
                    this.object_name = object.name;
                    this.object_city = object?.address?.city;
                    this.object_street = object?.address?.street;
                    this.object_note = object?.note || '';
                    this.object_housenumber = object?.address?.housenumber;
                    this.object_zipcode = object?.address?.zipcode;
                    this.selected_manager_id = object.manaer?.id || null; // Set the selected manager
                    this.object_archived = object.archived || false;
                    this.object_representation = object.representation || false;
                    if (object.manager) {
                        this.selected_manager_id = object.manager.id;
                        if (
                            !this.available_managers.find(
                                manager => manager.id === object.manager.id
                            )
                        ) {
                            this.available_managers.push({
                                id: object.manager.id,
                                name: `${object.manager.firstname} ${object.manager.lastname}`,
                            });
                        }
                    }
                }

                if (assigned_employees) {
                    this.object_assigned_employees = new Set(
                        assigned_employees.map((employee: any) => employee.id)
                    );

                    assigned_employees.forEach((employee: any) => {
                        if (!this.availableEmployees.has(employee.id)) {
                            this.availableEmployees.set(
                                employee.id,
                                `${employee.firstname} ${employee.lastname}`
                            );
                        }

                        if (this.object_assigned_employees.has(employee.id)) {
                            this.assignedEmployees.add(employee.id);
                        } else {
                            this.unassignedEmployees.add(employee.id);
                        }
                    });
                }

                if (assigned_products) {
                    this.object_assigned_products = new Set(
                        assigned_products
                            .filter((obj: any) => obj.excluded !== true)
                            .map((product: any) => product.id)
                    );
                    this.object_excluded_products = new Set(
                        assigned_products
                            .filter((obj: any) => obj.excluded === true)
                            .map((product: any) => product.id)
                    );

                    assigned_products.forEach((product: any) => {
                        if (!this.availableProducts.has(product.id)) {
                            this.availableProducts.set(product.id, product);
                        }

                        if (this.object_assigned_products.has(product.id)) {
                            this.assignedProducts.add(product.id);
                        } else {
                            this.unassignedProducts.add(product.id);
                        }
                    });
                }
            }
        });
    }

    public async fetchNewObject(): Promise<void> {
        runInAction(() => {
            this.objectDataLoading = true;
        });

        const objectNewDetails: any = await this.sdk.fetchApi('object', 'create', {});

        runInAction(() => {
            this.objectDataLoading = false;
            if (objectNewDetails?.status === 200) {
                const { assigned_products } = objectNewDetails.json || {};

                if (assigned_products) {
                    this.object_assigned_products = new Set(
                        assigned_products.map((product: any) => product.id)
                    );

                    assigned_products.forEach((product: any) => {
                        if (!this.availableProducts.has(product.id)) {
                            this.availableProducts.set(product.id, product);
                        }

                        if (this.object_assigned_products.has(product.id)) {
                            this.assignedProducts.add(product.id);
                        } else {
                            this.unassignedProducts.add(product.id);
                        }
                    });
                }
            }
        });
    }

    get debug() {
        return true;
    }

    public async fetchProducts(): Promise<void> {
        if (this.productDataFetched) {
            return;
        }

        // Reset
        runInAction(() => {
            this.productDataLoading = true;
            this.assignedProducts = new Set();
            this.unassignedProducts = new Set();
        });

        const productList: any = await this.sdk.fetchApi('product', 'query', {
            format: 'simple',
            limit: 999,
        });

        runInAction(() => {
            this.productDataLoading = false;

            if (productList?.status === 200) {
                this.productDataFetched = true;

                (productList.json.data as Array<any>).forEach((p: any) => {
                    if (!this.availableProducts.has(p.id)) {
                        this.availableProducts.set(p.id, p);
                    }

                    // determine if the product is assigned or unassigned
                    if (this.object_assigned_products.has(p.id)) {
                        this.assignedProducts.add(p.id);
                    } else {
                        this.unassignedProducts.add(p.id);
                    }
                });
            }
        });
    }

    public async onSaveObject(): Promise<void> {
        const data = {
            manager_id: this.selected_manager_id || undefined,
            address: {
                street: this.object_street,
                housenumber: this.object_housenumber,
                zipcode: this.object_zipcode,
                city: this.object_city,
            },
            note: this.object_note,
            name: this.object_name,
            object_id: this.object_id || undefined,
            object_number: this.object_number,
            assigned_employees: Array.from(this.assignedEmployees),
            assigned_products: Array.from(this.assignedProducts),
        };

        const addObjectRequest: any = await this.sdk.fetchApi('object', this.state, data);

        if (addObjectRequest?.status !== 201 && addObjectRequest?.status !== 200) {
            const errorCode = addObjectRequest?.json?.error || '';

            if (errorCode) {
                switch (errorCode) {
                    case 'ERR_VALIDATE_NAME':
                        throw new Error('Bitte gebe einen gültigen Objektnamen an!');
                    case 'ERR_VALIDATE_OBJECT_ID':
                        throw new Error('Bitte gebe eine valide 7-stellige Objekt-Id an!');
                    case 'ERR_VALIDATE_OBJECT_ID_EXIST':
                        throw new Error('Diese Objekt-Id wurde bereits vergeben!');
                    case 'ERR_VALIDATE_OBJECT_NAME_EXIST':
                        throw new Error('Dieser Objekt-Name wurde bereits vergeben!');
                    default:
                        break;
                }
            }

            throw new Error('Ein unbekannter Fehler ist aufgetreten');
        }
    }

    public async archiveObject(archived: boolean): Promise<void> {
        const response = await this.sdk.fetchApi('object', 'archive', {
            object_id: this.object_id,
            archived,
        });

        if (response?.status !== 200) {
            throw new Error(response?.json?.message || 'Fehler beim Archivieren des Objekts');
        }
    }

    public selectManager(manager_id: number): void {
        this.selected_manager_id = manager_id;
    }

    public updateEmployeeAssignment(
        unassigned: Array<Object_Simple>,
        assigned: Array<Object_Simple>
    ): void {
        this.assignedEmployees = new Set(assigned.map(obj => obj.id));
        this.unassignedEmployees = new Set(unassigned.map(obj => obj.id));
    }

    public resetProductsAssignment(): void {
        this.assignedProducts = new Set(this.assignedProducts);
        this.unassignedProducts = new Set(this.unassignedProducts);
    }

    public updateProductsAssignment(
        unassigned: Array<Object_Simple>,
        assigned: Array<Object_Simple>
    ): void {
        this.assignedProducts = new Set(assigned.map(obj => obj.id));
        this.unassignedProducts = new Set(unassigned.map(obj => obj.id));
    }

    public onChangeTab(tab: number): void {
        if (this.activeTab !== tab) {
            if (tab === 1) {
                this.fetchProducts();
            } else if (tab === 0) {
                this.fetchEmployees();
            }
            this.activeTab = tab;
        }
    }

    get assignedEmployeeArray(): Array<Object_Simple> {
        return Array.from(this.assignedEmployees).map((id: number) => {
            return {
                id: id,
                name: this.availableEmployees.get(id) || '?',
            };
        });
    }

    get unassignedEmployeeArray(): Array<Object_Simple> {
        return Array.from(this.unassignedEmployees).map((id: number) => {
            return {
                id: id,
                name: this.availableEmployees.get(id) || '?',
            };
        });
    }

    get assignedProductsArray(): Array<Object_Simple> {
        return Array.from(this.assignedProducts).map((id: number) => {
            return {
                id: id,
                name: this.availableProducts.get(id)?.name || '?',
                global: this.availableProducts.get(id)?.global || false,
            };
        });
    }

    get unassignedProductsArray(): Array<Object_Simple> {
        return Array.from(this.unassignedProducts).map((id: number) => {
            return {
                id: id,
                name: this.availableProducts.get(id)?.name || '?',
                global: this.availableProducts.get(id)?.global || false,
            };
        });
    }

    set street(street: string) {
        // limit to 40 characters
        if (street.length <= 80) {
            runInAction(() => {
                this.object_street = street;
            });
        }
    }

    get street(): string {
        return this.object_street;
    }

    set housenumber(housenumber: string) {
        // limit to 40 characters
        if (housenumber.length <= 10) {
            runInAction(() => {
                this.object_housenumber = housenumber;
            });
        }
    }

    get housenumber(): string {
        return this.object_housenumber;
    }

    set zipcode(zipcode: string) {
        // limit to 40 characters
        if (zipcode.length <= 5) {
            runInAction(() => {
                this.object_zipcode = zipcode;
            });
        }
    }

    get zipcode(): string {
        return this.object_zipcode;
    }

    set city(city: string) {
        // limit to 40 characters
        if (city.length <= 80) {
            runInAction(() => {
                this.object_city = city;
            });
        }
    }

    get city(): string {
        return this.object_city;
    }

    set objectname(name: string) {
        // limit to 40 characters
        if (name.length <= 80) {
            runInAction(() => {
                this.object_name = name;
            });
        }
    }

    get objectname(): string {
        return this.object_name;
    }

    set objectnote(txt: string) {
        if (txt.length <= 255) {
            runInAction(() => {
                this.object_note = txt;
            });
        }
    }

    get objectnote(): string {
        return this.object_note;
    }

    get archived(): boolean {
        return this.object_archived;
    }

    get saveButton(): string {
        return this.state === 'update' ? 'Aktualisieren' : 'Erstellen';
    }

    get isCloneable(): boolean {
        return this.state !== 'add' && !this.isRepresentation && !this.objectDataLoading;
    }

    get isArchivable(): boolean {
        return this.state !== 'add' && !this.isRepresentation && !this.objectDataLoading;
    }

    get header(): string {
        return this.state === 'update' ? 'Objekt bearbeiten' : 'Neues Objekt anlegen';
    }

    get id(): string {
        return 'object';
    }

    get isRepresentation(): boolean {
        return this.object_representation;
    }

    get isLoading(): boolean {
        return this.objectDataLoading;
    }

    get isInvalid(): boolean {
        return !(this.validObjectname && this.validObjectId);
    }

    get validObjectname(): boolean {
        return this.object_name.length >= 3;
    }

    get validObjectIdTooltip(): string {
        return (
            process.env.REACT_APP_OBJECT_VALIDATION_TOOLTIP ||
            'Eine gültige maximal 7 stellige Zahl eingeben.'
        );
    }

    get validObjectId(): boolean {
        if (typeof this.object_number !== 'number') return false;

        if (process.env.REACT_APP_OBJECT_VALIDATION_MAX_LENGTH) {
            const expectedLength: number = parseInt(
                process.env.REACT_APP_OBJECT_VALIDATION_MAX_LENGTH,
                10
            );
            if (expectedLength && `${this.object_number}`.length !== expectedLength) return false;
        }

        return true;
    }

    set objectid(id: string) {
        let expectedMaxLength: number = 7;

        if (process.env.REACT_APP_OBJECT_VALIDATION_MAX_LENGTH) {
            expectedMaxLength = parseInt(process.env.REACT_APP_OBJECT_VALIDATION_MAX_LENGTH, 10);
        }

        // limit to 40 characters
        if (id.length <= expectedMaxLength) {
            runInAction(() => {
                this.object_number = id ? parseInt(id, 10) : null;
            });
        }
    }

    get objectid(): string {
        return `${this.object_number || ''}`;
    }
}

export default ObjectDialogStore;
